import {Box, Center, getColorByPath, HStack, Icon, Show, type TextSize} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match, Pattern} from 'ts-pattern';

import {always} from 'ramda';

import {suffixTestId, type TestIdProps} from 'shared/web';

import {useComponentsTheme} from '../../hooks/useComponentsTheme';
import type {ColorSchemeType} from '../Flag/FlagColorScheme';
import {getColorsFromScheme} from '../Flag/getColorsFromScheme';
import {getFlagElementSizes} from '../Flag/getFlagElementSizes';

export interface TrendProps extends TestIdProps {
  label: string;
  isNegative?: boolean;
  isPositive?: boolean;
  isSubtle?: boolean;
  isTransparent?: boolean;
  isSmall?: boolean;
  isReverseIcon?: boolean;
}

const trendLabelVariants = cva('overflow-hidden whitespace-nowrap tracking-[0]', {
  variants: {
    size: {
      small: 'text-[length:var(--text-size-small)] leading-[var(--text-leading-small)]',
      xxSmall: 'text-[length:var(--text-size-xx-small)] leading-[var(--text-leading-xx-small)]',
    },
  },
});

export function Trend(props: TrendProps) {
  const theme = useComponentsTheme();
  const colorScheme = match<[boolean | undefined, boolean | undefined], ColorSchemeType>([
    props.isPositive,
    props.isNegative,
  ])
    .with([true, Pattern.any], always('green'))
    .with([Pattern.any, true], always('red'))
    .otherwise(always('neutral'));

  const {backgroundColor, color} = getColorsFromScheme(
    colorScheme,
    props.isSubtle || props.isTransparent
  );
  const {height, labelHorizontalPadding} = getFlagElementSizes(props.isSmall ? 'small' : 'default');
  const textSize: TextSize = props.isSmall ? 'xxSmall' : 'small';

  const isPositive = props.isReverseIcon ? props.isNegative : props.isPositive;
  const isNegative = props.isReverseIcon ? props.isPositive : props.isNegative;

  return (
    <HStack justify="flex-start">
      <Box
        borderRadius="small"
        height={height}
        backgroundColor={props.isTransparent ? undefined : backgroundColor}
        paddingHorizontal={labelHorizontalPadding}
        data-testid={suffixTestId('trend', props)}
      >
        <Center height="100%">
          <HStack justify="space-between" align="center" height="100%" spacing={1}>
            <Show when={isPositive}>
              <Icon value="navigation/arrow_upward" size={3} color={color} />
            </Show>
            <Show when={isNegative}>
              <Icon value="navigation/arrow_downward" size={3} color={color} />
            </Show>
            <span
              className={cn(trendLabelVariants({size: textSize}))}
              style={{color: getColorByPath(color, theme) ?? 'transparent'}}
            >
              {props.label}
            </span>
          </HStack>
        </Center>
      </Box>
    </HStack>
  );
}
